import React from 'react';
import { Box, Typography, Button } from '@mui/material';

interface EnterpriseLicenseHeaderProps {
  onUploadClick: () => void;
  hasPreloadedFiles: boolean;
}

/**
 * Renders the "Enterprise License Intelligence" info-banner with an Upload button.
 *
 * Placement: Rendered between the Tabs and the data table inside
 * DataLandscapeForm's renderTechnologyUsageTab().
 *
 * Usage:
 *   <EnterpriseLicenseHeader onUploadClick={() => setUploadModalOpen(true)} />
 */
const EnterpriseLicenseHeader: React.FC<EnterpriseLicenseHeaderProps> = ({
  onUploadClick,
  hasPreloadedFiles,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid #E0E6FF',
        borderRadius: '10px',
        backgroundColor: '#F6FAFF',
        px: 2.5,
        py: 1.75,
        mb: 3,
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {/* Left: Icon + Text */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src="/images/license-stars.svg" alt="license-upload-icon" width={25} height={25} />
        </Box>

        <Box>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              fontSize: 18,
              color: '#1532B8',
              lineHeight: 1.4,
            }}
          >
            Enterprise License Intelligence
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: 16,
              color: '#4D5358',
              lineHeight: 1.5,
              mt: 0.25,
            }}
          >
            Upload documents or licenses to identify and populate enterprise application modules, and technology vendors across functions.
          </Typography>
        </Box>
      </Box>

      {/* Right: Upload button */}
      <Button
        variant="outlined"
        onClick={onUploadClick}
        sx={{
          textTransform: 'none',
          fontFamily: 'Poppins, sans-serif',
          fontSize: 14,
          fontWeight: 500,
          color: '#1A55F9',
          borderColor: '#1A55F9',
          borderRadius: '8px',
          px: 2.5,
          py: 0.75,
          flexShrink: 0,
          '&:hover': {
            backgroundColor: '#EEF3FF',
            borderColor: '#1444d0',
          },
        }}
      >
        {hasPreloadedFiles ? 'Manage' : 'Upload'}
      </Button>
    </Box>
  );
};

export default EnterpriseLicenseHeader;
